/*
 * Maps failed assistant REST calls to a user-facing outcome. Pure so it can be
 * unit-tested; the provider applies the outcome (inline error, refetch, hide).
 */

/** Backend limit on a chat message's `content`; also enforced in the composer. */
export const MAX_MESSAGE_LENGTH = 8000;
/** Backend limit on `question_response.selected_options`. */
export const MAX_SELECTED_OPTIONS = 20;

export type AssistantRequestKind =
  'message' | 'question' | 'action' | 'history' | 'list';

/** What went wrong, as shown inline in the panel (see Panel's errorText). */
export type AssistantErrorCode =
  | 'generic'
  | 'rate_limited'
  | 'invalid_message'
  | 'invalid_answer'
  | 'action_conflict'
  | 'question_conflict'
  | 'history_failed'
  | 'list_failed'
  | 'too_many_options';

export interface AssistantErrorOutcome {
  /** Error to show, or null when nothing is shown. */
  code: AssistantErrorCode | null;
  /** The user lost access: hide and disable the assistant. */
  forbidden: boolean;
  /** Server state moved on (e.g. action already decided): reload the transcript. */
  refetch: boolean;
}

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status;
}

function codeOf(error: unknown): string | undefined {
  return (error as { response?: { data?: { code?: string } } } | null)?.response
    ?.data?.code;
}

export function classifyAssistantError(
  error: unknown,
  kind: AssistantRequestKind
): AssistantErrorOutcome {
  const status = statusOf(error);
  const outcome: AssistantErrorOutcome = {
    code: 'generic',
    forbidden: false,
    refetch: false,
  };

  if (status === 403) {
    // A credits_exhausted 403 already gets the global billing toast.
    if (codeOf(error) === 'credits_exhausted')
      return { ...outcome, code: null };
    return { code: null, forbidden: true, refetch: false };
  }
  if (status === 429) return { ...outcome, code: 'rate_limited' };
  if (status === 409 && (kind === 'action' || kind === 'question')) {
    return {
      ...outcome,
      code: kind === 'action' ? 'action_conflict' : 'question_conflict',
      refetch: true,
    };
  }
  if (status === 400) {
    return {
      ...outcome,
      code: kind === 'message' ? 'invalid_message' : 'invalid_answer',
    };
  }
  if (kind === 'history') return { ...outcome, code: 'history_failed' };
  if (kind === 'list') return { ...outcome, code: 'list_failed' };
  return outcome;
}
